import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, Download, Filter, Plus } from 'lucide-react';
import Button from '../components/atoms/Button';
import Loading from '../components/atoms/Loading';
import { fetchQueries } from '../redux/querymanagement/actions';
import {useNavigate} from 'react-router-dom';


// Demo response for GET /api/labelfields/:id. Replace this constant with the API response.
const demoLabelFields = [
  { id: 1, label: 'Name', key: 'name' },
  { id: 2, label: 'Email', key: 'email' },
  { id: 3, label: 'Query Type', key: 'queryType' },
  { id: 4, label: 'Description', key: 'description' },
  { id: 5, label: 'Employee', key: 'employee' },
  { id: 6, label: 'Status', key: 'status' },
  { id: 7, label: 'Escalation To', key: 'escalationTo' },
];

// Demo response for the query records API. The label-field id is ready for future requests.
const demoTableData = [
  { id: 1, name: 'Rajesh Kumar', email: 'rajesh@gmail.com', queryType: 'shift_timing', description: 'Need to upload hono hr app in new phone Emp id- 90998989', employee: 'Employee', status: 'Open', escalationTo: 'Level 1' },
  { id: 2, name: 'Deepanshu', email: 'deepanshu@gmail.com', queryType: 'shift_timing', description: 'Need to upload hono hr app in new phone Emp id- 90998989', employee: 'Employee', status: 'Open', escalationTo: 'Level 1' },
  { id: 3, name: 'Ritik Kumar', email: 'ritik@gmail.com', queryType: 'shift_timing', description: 'Need to upload hono hr app in new phone Emp id- 90998989', employee: 'Employee', status: 'Closed', escalationTo: 'Level 2' },
  { id: 4, name: 'Raj Kumar', email: 'raj@gmail.com', queryType: 'shift_timing', description: 'Need to upload hono hr app in new phone Emp id- 90998989', employee: 'Employee', status: 'Open', escalationTo: 'Level 1' },
];

const labelFieldId = 44;
const statusOptions = ['Open', 'Closed'];
const escalationOptions = ['Level 1', 'Level 2', 'Level 3'];
const STATUS_FIELD = { id: 'status', label: 'Status', key: 'status' };
const ESCALATION_FIELD = { id: 'escalationTo', label: 'Escalation To', key: 'escalationTo' };

function resolveComplaintTypeId(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value.data ?? value.id ?? null;
  return value;
}

function mapComplaintRow(complaint) {
  const row = {
    id: complaint?.complaintId,
    status: complaint?.status ?? '',
    escalationTo: complaint?.level != null && complaint?.level !== ''
      ? `Level ${complaint.level}`
      : '',
  };
  (complaint?.formField || []).forEach((field) => {
    const key = field.fieldKey || field.fieldName;
    if (!key) return;
    row[key] = field.value;
  });
  return row;
}

function cellClass(key, isHeader = false) {
  const classes = [];
  if (!isHeader && key === 'description') classes.push('ca-query-table__desc');
  if (key === 'status') classes.push('ca-query-table__sticky', 'ca-query-table__sticky--status');
  if (key === 'escalationTo') classes.push('ca-query-table__sticky', 'ca-query-table__sticky--escalation');
  return classes.length ? classes.join(' ') : undefined;
}

function renderCell(field, row) {
  const value = row[field.key] ?? '-';

  if (field.key === 'status') {
    const options = statusOptions.includes(value) ? statusOptions : [value, ...statusOptions];
    return (
      <select className="ca-query-table__select" defaultValue={value} aria-label="Status">
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }

  if (field.key === 'escalationTo') {
    const options = escalationOptions.includes(value) ? escalationOptions : [value, ...escalationOptions];
    return (
      <select className="ca-query-table__select" defaultValue={value} aria-label="Escalation to">
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }

  return value;
}

export default function QueryTable({ id = labelFieldId, labelFields = demoLabelFields, tableData = demoTableData }) {
  const dispatch = useDispatch();
  const complaintTypeId = resolveComplaintTypeId(useSelector((store) => store.appDetails?.id));
  const {
    list = [],
    isLoading = false,
    error = null,
    hasLoaded = false,
  } = useSelector((store) => store.queryManagement ?? {});

  useEffect(() => {
    if (!complaintTypeId) return undefined;
    dispatch(fetchQueries({
      complaintTypeId,
      page: 1,
      limit: 10,
    }));
    return undefined;
  }, [complaintTypeId, dispatch]);

  const mappedRows = useMemo(
    () => list.map((complaint) => ({
      ...mapComplaintRow(complaint),
      _fields: complaint?.formField || [],
    })),
    [list],
  );

  const apiColumns = useMemo(() => {
    const seen = new Set();
    const columns = [];
    mappedRows.forEach((row) => {
      (row._fields || []).forEach((field) => {
        const key = field.fieldKey || field.fieldName;
        if (!key || key === 'status' || key === 'escalationTo' || seen.has(key)) return;
        seen.add(key);
        columns.push({
          id: key,
          label: field.fieldName || key,
          key,
        });
      });
    });
    return columns;
  }, [mappedRows]);

  const resolvedFields = hasLoaded || complaintTypeId
    ? [...apiColumns, STATUS_FIELD, ESCALATION_FIELD]
    : labelFields;
  const resolvedRows = hasLoaded
    ? mappedRows.map(({ _fields, ...row }) => row)
    : (complaintTypeId ? [] : tableData);
  const scrollFields = resolvedFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo');
  const stickyFields = [STATUS_FIELD, ESCALATION_FIELD];

  // const dynamicFields = labelFields.filter((field) => field.key !== 'status' && field.key !== 'escalationTo');
  // 654w3a
  const navigate = useNavigate();
  const handleNew =()=>{
    console.log('New Query button clicked');
    navigate('/querymanagement/new/33');
  };
  return (
    <section className="ca-query-table" aria-label="Query records">
      <div className="ca-query-table__toolbar">
        <label className="ca-query-table__filter">
          <Filter size={14} aria-hidden="true" />
          <span className="ca-query-table__sr-only">Filter by status</span>
          <select defaultValue="all" aria-label="Filter by status">
            <option value="all">Select Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <div className="ca-query-table__actions">
          <Button variant="outline" onClick={() => handleNew()} size="sm" startIcon={<Plus size={14} />}>
         
            New Query
        
          </Button>
          <Button variant="outline" size="sm" startIcon={<Download size={14} />}>
            Download Report
          </Button>
        </div>
      </div>

      {error ? <p className="ca-body">{error}</p> : null}
      {isLoading && !resolvedRows.length ? (
        <Loading text="Loading queries" />
      ) : (
      <div className="ca-query-table__scroll" data-label-field-id={id}>
        <table>
          <thead>
            <tr>
              <th scope="col">#</th>
              {scrollFields.map((field) => (
                <th scope="col" key={field.id} className={cellClass(field.key, true)}>{field.label}</th>
              ))}
              <th className="ca-query-table__spacer" aria-hidden="true" />
              {stickyFields.map((field) => (
                <th scope="col" key={field.id} className={cellClass(field.key, true)}>{field.label}</th>
              ))}
              {/* <th className="ca-query-table__sticky-column ca-query-table__sticky-column--status" scope="col">{statusField.label}</th> */}
              {/* <th className="ca-query-table__sticky-column ca-query-table__sticky-column--escalation" scope="col">{escalationField.label}</th> */}
            </tr>
          </thead>
          <tbody>
            {resolvedRows.map((row, index) => (
              <tr key={row.id ?? index}>
                <td>{index + 1}</td>
                {scrollFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={cellClass(field.key)}
                  >
                    {renderCell(field, row)}
                  </td>
                ))}
                <td className="ca-query-table__spacer" aria-hidden="true" />
                {stickyFields.map((field) => (
                  <td
                    key={`${row.id ?? index}-${field.id}`}
                    className={cellClass(field.key)}
                  >
                    {renderCell(field, row)}
                  </td>
                ))}
                {/* <td className="ca-query-table__sticky-column ca-query-table__sticky-column--status">
                  <span className="ca-query-table__select-value">{row[statusField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td>
                <td className="ca-query-table__sticky-column ca-query-table__sticky-column--escalation">
                  <span className="ca-query-table__select-value">{row[escalationField.key] ?? '-'}</span>
                  <ChevronDown size={14} aria-hidden="true" />
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}
